import { useMemo } from 'react'
import moment from 'moment'
import { groupBy } from 'lodash'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'

import { Accordion, Card, CardColors, Text } from '@/ui'
import { EstimateCard } from '../shared/EstimateCard'
import { TypographyStyles } from '@/styles'
import { useCompleteQTEActionItemIfNeeded } from '@/features/taxes/quarterly/service'
import { selectPreviousQuarterTaxEstimatesForYear } from '@/features/taxes/quarterly/userTaxEstimates.selector'

export const PreviousTaxPayments = ({
  taxYear,
  taxQuarter,
  heading,
  note,
  explainer,
  federalOnly = false,
  completesChecklistItem = false,
  onPaymentSaved,
}: {
  taxYear: number
  taxQuarter: number
  heading: string
  note?: string
  explainer: string
  federalOnly?: boolean
  completesChecklistItem?: boolean
  onPaymentSaved?: () => void
}) => {
  const estimates = useReselector(
    selectPreviousQuarterTaxEstimatesForYear,
    taxYear,
    taxQuarter
  )
  const groupedEstimates = useMemo(
    () =>
      Object.entries(
        groupBy(
          // The selector filters on quarter number only, so a state row for an
          // earlier quarter arrives here too.
          federalOnly
            ? estimates.filter((estimate) => estimate.type === 'federal')
            : estimates,
          (estimate) => estimate.taxQuarter
        )
      ),
    [estimates, federalOnly]
  )

  useCompleteQTEActionItemIfNeeded({ enabled: completesChecklistItem })

  return (
    <div className="w-full space-y-6">
      <div className="w-full max-w-4xl space-y-2">
        <Text variant={TypographyStyles.H2}>{heading}</Text>
        {note && <Text variant={TypographyStyles.BODY_MD}>{note}</Text>}
      </div>

      <Accordion
        className="w-full max-w-4xl"
        variant="text"
        type="single"
        collapsible
        items={[
          {
            title: 'Why does Heard need this?',
            content: explainer,
          },
        ]}
      />

      {groupedEstimates.map(([key, estimates]) => (
        <div key={key} className="w-full">
          <Card color={CardColors.LEVEL_0} className="space-y-2">
            <Text variant={TypographyStyles.H2}>
              {moment(key, DATE_FORMATS.YEAR_QUARTER).format(
                DATE_FORMATS.QUARTER_PREFIX_YEAR
              )}
            </Text>
            {estimates.map((estimate) => (
              <EstimateCard
                key={estimate.id}
                estimate={estimate}
                onPaymentSaved={onPaymentSaved}
              />
            ))}
          </Card>
        </div>
      ))}
    </div>
  )
}
